import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { constant } from '@config/BaseSetting';
import useLocalStorage from '@hooks/useLocalStorage';
import { createSearchParams, useNavigate } from 'react-router-dom';
import { organizationInformationPath, resellersListPath } from '@config/Routes/WebClientRoutes';
import {
  setOrganizationDetailState,
  setPartnerDetailsState,
} from '@features/application/applicationSlice';
import { fetchRestoreStatusListAsync } from '@features/restoreStatus/restoreStatusInfoSlice';
import {
  EMAIL_RESTORE,
  EMAIL_RESTORE_COLUMNS,
} from '@pages/RestoreStatusPage/pageHooks/constants/emailRestoreConstants';
import useFeatureFlag from '@hooks/useFeatureFlag';
import { ENTRA_ID_RESTORE, ENTRA_ID_RESTORE_COLUMNS } from './constants/entraidRestoreConstants';

const defaultSkippedItemsState = {
  showModal: false,
  selectedRestore: {},
  restoreType: '',
};

export default function useRestoreStatusFetching(props) {
  const { defaultFilter, setFilterState, defaultPagination, setPaginationState } = props;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [accessTokenSet] = useLocalStorage(constant.ACCESS_TOKEN_STORAGE);
  const [isReset, setIsReset] = useState(false);
  const [showMesageDialog, setShowMesageDialog] = useState(false);
  const [messageDialog, setMesageDialog] = useState({ title: '', message: '' });
  const [skippedItemsModalState, setSkippedItemsModalState] = useState(defaultSkippedItemsState);
  const { featureFlags, hasPermission } = useFeatureFlag();

  const handleFetchRestoreStatusListAsync = (
    pageIndex,
    pageSize,
    resellerId,
    OrganizationOwnerId,
    restoreType,
    filters
  ) => {
    dispatch(
      fetchRestoreStatusListAsync({
        accessToken: accessTokenSet,
        reset: true,
        pageNumber: pageIndex,
        pageSize,
        resellerId,
        OrganizationOwnerId,
        restoreType,
        filters,
      })
    );
  };

  const handleResetFilter = () => {
    setIsReset(true);
  };

  useEffect(() => {
    if (isReset) {
      setFilterState(defaultFilter);
      setPaginationState(defaultPagination);
    }
    setIsReset(false);
  }, [isReset]);

  const tableColumns = (restoreType) => {
    let columns = [];
    switch (restoreType) {
      case EMAIL_RESTORE:
        columns = EMAIL_RESTORE_COLUMNS;
        break;
      case ENTRA_ID_RESTORE:
        columns = ENTRA_ID_RESTORE_COLUMNS;
        break;
      default:
        columns = EMAIL_RESTORE_COLUMNS;
        break;
    }

    // add click able function
    return columns.map((item) => {
      if (item.columnName === 'organizationName') {
        // eslint-disable-next-line no-param-reassign
        item.onClickCell = (row) => {
          handleResetFilter();
          dispatch(
            setOrganizationDetailState({
              organizationInformationPath,
              row: { organizationOwnerId: row.organizationOwnerId, orgName: row.organizationName },
            })
          );
          return navigate({
            pathname: organizationInformationPath,
            search: createSearchParams({
              id: row.organizationId,
              name: row.organizationName,
            }).toString(),
          });
        };
      }
      if (item.columnName === 'partnerName') {
        // eslint-disable-next-line no-param-reassign
        item.onClickCell = (row) => {
          handleResetFilter();
          dispatch(
            setPartnerDetailsState({
              resellersListPath,
              row: { id: row.partnerId },
            })
          );
          return navigate({
            pathname: resellersListPath,
            search: createSearchParams({
              id: row.partnerId,
              name: row.partnerName,
            }).toString(),
          });
        };
      }
      if (
        hasPermission(featureFlags.RestoreSkippedItems) &&
        item.columnName === 'skippedItems' &&
        restoreType === EMAIL_RESTORE
      ) {
        // eslint-disable-next-line no-param-reassign
        item.onClickCell = (row) => {
          setSkippedItemsModalState({
            showModal: true,
            selectedRestore: row,
            restoreType,
          });
          return '';
        };
      } else if (
        item.columnName === 'errorMessage' ||
        item.columnName === 'skippedItems' ||
        item.columnName === 'queryParams' ||
        item.columnName === 'additionalData' ||
        item.columnName === 'notes'
      ) {
        // eslint-disable-next-line no-param-reassign
        item.onClickCell = (row) => {
          setShowMesageDialog(true);
          setMesageDialog({ title: item.label, message: row[item.columnName] });
          return '';
        };
      }

      return item;
    });
  };

  const handleCloseSkippedItemsModal = () => setSkippedItemsModalState(defaultSkippedItemsState);

  return {
    handleFetchRestoreStatusListAsync,
    tableColumns,
    showMesageDialog,
    setShowMesageDialog,
    messageDialog,
    skippedItemsModalState,
    handleCloseSkippedItemsModal,
  };
}
